// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Renderer-component wrappers for legislative-review bill renderers.
 *
 * Each adapts an existing Bill*Content component to the registry contract:
 * pull the dataset payload off `slotData`, unwrap `payload.output` if present
 * (the LR convention), pass to the existing content component.
 */
import { type FC } from 'react'
import type { DatasetResponse } from '@/subsystems/documents/services'
import type { RendererComponentProps } from '@/app-shell/renderer-components'
import { BillFindingsContent } from '../components/dialogs/BillFindingsDialog'
import { BillReviewContent } from '../components/dialogs/BillReviewDialog'
import { BillCostEstimateContent } from '../components/dialogs/BillCostEstimateDialog'
import { BillDepartmentRoutingContent } from '../components/dialogs/BillDepartmentRoutingDialog'

/**
 * LR datasets wrap the model output as `payload.output`. Older payloads put
 * the data at the top level. Tolerate both.
 */
function unwrap(response: DatasetResponse | null | undefined): unknown {
  if (!response?.payload) return null
  const payload = response.payload as Record<string, unknown>
  if (payload.error) return null
  if (payload.output && typeof payload.output === 'object') return payload.output
  return payload
}

/**
 * Pre-migration tolerance: existing renderer configs may use ad-hoc slot keys
 * (the slot-key contract didn't exist). Until the schema-normalizer migration
 * runs, fall back to the first dataset slot's payload.
 */
function pickResponse(slotData: Record<string, DatasetResponse | null> | undefined, slotKey: string): DatasetResponse | null {
  if (!slotData) return null
  const direct = slotData[slotKey]
  if (direct) return direct
  for (const value of Object.values(slotData)) {
    if (value) return value
  }
  return null
}

function makeBillRenderer(
  slotKey: string,
  Content: FC<{ data: unknown }>,
): FC<RendererComponentProps> {
  return ({ slotData }) => <Content data={unwrap(pickResponse(slotData, slotKey))} />
}

export const BillFindingsRenderer = makeBillRenderer('findings', BillFindingsContent)
export const BillReviewRenderer = makeBillRenderer('review', BillReviewContent)
export const BillCostEstimateRenderer = makeBillRenderer('costs', BillCostEstimateContent)
export const BillDepartmentRoutingRenderer = makeBillRenderer('routing', BillDepartmentRoutingContent)
